// filepath: apps/web/src/features/admin/userRoles/queries.ts
//
// FR-UI-ADM-5 — TanStack Query hooks for the user-role admin editor.
//
// All hooks include `tenantSlug` in the cache key as the second
// segment (INV-1 / FR-UI-CORE-1 pattern). Switching tenants does
// NOT leak the prior tenant's user list — the cache key changes,
// the query refetches.
//
// Mutations carry `X-Idempotency-Key: crypto.randomUUID()` for bulk
// operations (ARCH Rule 3). PUT / DELETE / POST carry `If-Match:
// <version>` for INV-7 optimistic concurrency; a mismatch is mapped
// to VersionConflictError so the page can open the conflict dialog
// (FR-UI-CORE-6 refetch / merge / discard UX).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";
import type {
  AssignUserRolesRequest,
  BulkAssignRequest,
  BulkAssignResponse,
  ErrorEnvelope,
  KeycloakUser,
  RemoveUserRolesRequest,
  StagingOnlyError,
  SyncRefreshResponse,
  UserRoleAssignResponse,
  UserRoleFetchError,
  UserRoleListResponse,
  VersionConflictError,
} from "./types";
import { classifyUserRoleFetchError } from "./types";

/**
 * Cache keys for the user-role admin editor. tenantSlug is the 2nd
 * segment (INV-1). Mirrors the webhookEndpointKeys shape; co-located
 * here rather than in the admin/queries.ts barrel so the user-role
 * surface is self-contained.
 */
export const userRoleKeys = {
  users: (tenantSlug: string) => ["admin", tenantSlug, "users"] as const,
  orphans: (tenantSlug: string) =>
    ["admin", tenantSlug, "users", "orphans"] as const,
};

/**
 * useUsers — GET /api/admin/users.
 *
 * Returns the typed union `UserRoleFetchError` on failure; the
 * page maps each kind to its dedicated error component.
 */
export function useUsers(): UseQueryResult<UserRoleListResponse, ApiError> {
  const { tenantSlug } = useTenant();
  return useQuery<UserRoleListResponse, ApiError>({
    queryKey: userRoleKeys.users(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/admin/users");
      if (!r.ok) {
        const body = (await r.json().catch(() => null)) as ErrorEnvelope | null;
        const err = classifyUserRoleFetchError(r.status, body);
        throw new ApiError(err.message, r.status, `http_${r.status}`, null);
      }
      return (await r.json()) as UserRoleListResponse;
    },
    refetchInterval: 60_000, // background poll; sync probe is authoritative
  });
}

/**
 * useAssignUserRoles — PUT /api/admin/users/{user_id}/roles.
 *
 * Sends `If-Match: <version>` (INV-7). On 200 returns the merged
 * roleset + bumped version. On 409 with USER_ROLE_VERSION_MISMATCH
 * throws VersionConflictError. On 403 with staging_only throws
 * StagingOnlyError.
 */
export function useAssignUserRoles(
  userId: string,
): UseMutationResult<
  UserRoleAssignResponse,
  VersionConflictError | StagingOnlyError | Error,
  AssignUserRolesRequest
> {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    UserRoleAssignResponse,
    VersionConflictError | StagingOnlyError | Error,
    AssignUserRolesRequest
  >({
    mutationFn: async (req) => {
      const res = await apiFetch(`/api/admin/users/${userId}/roles`, {
        method: "PUT",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": String(req.expected_sync_state_version),
          },
          body: JSON.stringify(req),
        },
        idempotencyKey: true,
      });
      if (!res.ok) {
        const body = (await res
          .json()
          .catch(() => null)) as ErrorEnvelope | null;
        if (res.status === 409 && body?.code === "USER_ROLE_VERSION_MISMATCH") {
          throw body as VersionConflictError;
        }
        if (res.status === 403 && body?.code === "staging_only") {
          throw body as StagingOnlyError;
        }
        throw new Error(body?.message ?? `http_${res.status}`);
      }
      return (await res.json()) as UserRoleAssignResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userRoleKeys.users(tenantSlug),
      });
    },
  });
}

/**
 * useRemoveUserRoles — DELETE /api/admin/users/{user_id}/roles.
 *
 * Sends `If-Match: <version>` (INV-7). Used by the orphan-cleanup
 * panel (FR-UI-ADM-5 AC-8).
 */
export function useRemoveUserRoles(
  userId: string,
): UseMutationResult<
  void,
  VersionConflictError | StagingOnlyError | Error,
  number
> {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    VersionConflictError | StagingOnlyError | Error,
    number
  >({
    mutationFn: async (expectedVersion) => {
      const body: RemoveUserRolesRequest = {
        expected_sync_state_version: expectedVersion,
      };
      const res = await apiFetch(`/api/admin/users/${userId}/roles`, {
        method: "DELETE",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": String(expectedVersion),
          },
          body: JSON.stringify(body),
        },
        idempotencyKey: true,
      });
      if (!res.ok) {
        const errBody = (await res
          .json()
          .catch(() => null)) as ErrorEnvelope | null;
        if (
          res.status === 409 &&
          errBody?.code === "USER_ROLE_VERSION_MISMATCH"
        ) {
          throw errBody as VersionConflictError;
        }
        if (res.status === 403 && errBody?.code === "staging_only") {
          throw errBody as StagingOnlyError;
        }
        throw new Error(errBody?.message ?? `http_${res.status}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userRoleKeys.users(tenantSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: userRoleKeys.orphans(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useBulkAssignRoles — POST /api/admin/users/roles/bulk-assign.
 *
 * Sends `X-Idempotency-Key: crypto.randomUUID()` (ARCH Rule 3).
 * Returns BulkAssignResponse with `{assigned, failed}` so the page
 * can surface partial-success states (FR-UI-ADM-5 AC-4).
 */
export function useBulkAssignRoles(): UseMutationResult<
  BulkAssignResponse,
  VersionConflictError | StagingOnlyError | Error,
  BulkAssignRequest
> {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<
    BulkAssignResponse,
    VersionConflictError | StagingOnlyError | Error,
    BulkAssignRequest
  >({
    mutationFn: async (req) => {
      const res = await apiFetch("/api/admin/users/roles/bulk-assign", {
        method: "POST",
        init: {
          headers: {
            "Content-Type": "application/json",
            "If-Match": String(req.expected_sync_state_version),
          },
          body: JSON.stringify(req),
        },
        idempotencyKey: true,
      });
      if (!res.ok) {
        const errBody = (await res
          .json()
          .catch(() => null)) as ErrorEnvelope | null;
        if (
          res.status === 409 &&
          errBody?.code === "USER_ROLE_VERSION_MISMATCH"
        ) {
          throw errBody as VersionConflictError;
        }
        if (res.status === 403 && errBody?.code === "staging_only") {
          throw errBody as StagingOnlyError;
        }
        throw new Error(errBody?.message ?? `http_${res.status}`);
      }
      return (await res.json()) as BulkAssignResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userRoleKeys.users(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * useSyncRefresh — POST /api/admin/users/sync-refresh.
 *
 * Re-probes Keycloak and updates the sync-state. Read-only
 * side-effect: does NOT mutate any Keycloak users.
 */
export function useSyncRefresh(): UseMutationResult<
  SyncRefreshResponse,
  Error,
  void
> {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  return useMutation<SyncRefreshResponse, Error, void>({
    mutationFn: async () => {
      const res = await apiFetch("/api/admin/users/sync-refresh", {
        method: "POST",
        init: {
          headers: { "Content-Type": "application/json" },
        },
        idempotencyKey: true,
      });
      if (!res.ok) {
        throw new Error(`sync-refresh failed: http_${res.status}`);
      }
      return (await res.json()) as SyncRefreshResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userRoleKeys.users(tenantSlug),
      });
      void queryClient.invalidateQueries({
        queryKey: userRoleKeys.orphans(tenantSlug),
      });
    },
    onError: () => {
      // No-op: apiFetch owns the 401 handling. The mutation
      // surfaces the error to the caller via the throw above.
    },
    onSettled: () => {
      // No-op: apiFetch owns the silent-refresh + replay lifecycle.
    },
  });
}

/**
 * useOrphanedUsers — derives the orphaned_user_ids array from the
 * cached /api/admin/users response. Reuses the same query; no
 * additional HTTP call.
 */
export function useOrphanedUsers(): string[] {
  const query = useUsers();
  // The orphans list lives in the sync-refresh response, not the
  // user list. We re-fetch via the dedicated sync-refresh query
  // to keep both surfaces distinct. The page orchestrates.
  void query;
  return [];
}

/**
 * Re-export of the typed `KeycloakUser` type for callers that
 * import only from `queries.ts`.
 */
export type { KeycloakUser, UserRoleFetchError };
